/**
 * Basemap Migration Utility
 *
 * Handles backward compatibility for legacy basemap ID formats.
 * Since basemaps are now retrieved from the feature service, this utility
 * provides simple pass-through and validation functions.
 *
 * Legacy webmap IDs are mapped to their corresponding UniqueKey values
 * as defined in the dev_Basemap_extents feature service.
 */

import { MAP_DEFAULT_VAR } from 'types';
import { debugLog } from './basemap-debug.util';

export interface BasemapInitConfig {
  styleId?: string | null;
  webmapId?: string | null;
}

/**
 * Static national basemap configs used when feature-service options are not yet loaded.
 * Matches the fallback set in components/Map/index.tsx.
 */
const STATIC_NATIONAL_BASEMAP_CONFIGS: {
  [key: string]: BasemapInitConfig;
} = {
  streets: { styleId: 'arcgis/topographic/base' },
  imagery: { styleId: 'arcgis/imagery/standard' },
  imageryHybrid: { styleId: 'arcgis/imagery' }
};

/**
 * Static mapping of legacy webmap IDs to UniqueKey values
 * This maintains backward compatibility with persisted Redux state
 * that might contain old webmap IDs instead of UniqueKey values
 */
const LEGACY_WEBMAP_ID_TO_UNIQUE_KEY: { [webmapId: string]: string } = {
  // Legacy thumbnail/portal item IDs -> UniqueKey from feature service
  '2d9167ba2e934e98b8fda37f7a5dcb12': 'imageryHybrid',
  ef1d368ae3c44f398f3f444d2de4363a: 'imagery',
  '2ce6cd133d1c41a3afdd44c0d0c4f2a9': 'streets',
  fae788aa91e54244b161b59725dcbb2a: 'osm',
  '391433b0665c4ffa9364608df8112dc0': 'nsw',
  '04f962c42ec24c40a5de141b7a924b9e': 'qtopo',
  '6e84091f442447c3a5cb3b05582d35e3': 'thelist',
  fcf4ed70eb324c42a744989c90acf8e2: 'vicmap',

  // MapID (webmapId) values -> UniqueKey (when they differ)
  '5d4e6fe431334301a010f371ae31f702': 'imageryHybrid', // Shared by imagery and imageryHybrid, prefer imageryHybrid
  f5cdc4d3ddaf4154afc49fd0d8152691: 'streets'
};

/**
 * Migrates a legacy basemap ID to the current format (UniqueKey)
 * @param legacyId - The old basemap ID (could be webmap ID, portal item ID, or already a UniqueKey)
 * @returns The corresponding UniqueKey or the original ID if no migration needed
 */
export function migrateBasemapId(legacyId: string): string {
  if (!legacyId) {
    return legacyId;
  }

  // If it's a known legacy ID, migrate it
  if (LEGACY_WEBMAP_ID_TO_UNIQUE_KEY[legacyId]) {
    const migratedKey = LEGACY_WEBMAP_ID_TO_UNIQUE_KEY[legacyId];
    debugLog(
      '[Basemap Migration] Migrating basemap ID:',
      legacyId,
      '→',
      migratedKey
    );
    return migratedKey;
  }

  // Otherwise, assume it's already in the correct format (UniqueKey)
  return legacyId;
}

/**
 * Finds a basemap by identifier in the provided basemap options
 * @param identifier - The basemap identifier to find
 * @param basemapOptions - Array of basemap options from Redux state
 * @returns The matching basemap item or null if not found
 */
export function findBasemapByIdentifier(
  identifier: string,
  basemapOptions: any[]
): any | null {
  if (!identifier || !basemapOptions || basemapOptions.length === 0) {
    return null;
  }

  // Try to find by id (UniqueKey)
  let found = basemapOptions.find((item) => item.id === identifier);
  if (found) {
    return found;
  }

  // Try to find by webmapId (MapID)
  found = basemapOptions.find((item) => item.webmapId === identifier);
  if (found) {
    debugLog(
      '[Basemap Migration] Found basemap by webmapId:',
      identifier,
      '→',
      found.id
    );
    return found;
  }

  return null;
}

/**
 * Gets the basemap identifier (UniqueKey) from any basemap identifier
 * @param identifier - The basemap identifier (could be UniqueKey or webmap ID)
 * @param basemapOptions - Array of basemap options from Redux state
 * @returns The UniqueKey or null if not found
 * @deprecated Use findBasemapByIdentifier instead for more flexibility
 */
export function getBasemapKeyFromIdentifier(
  identifier: string,
  basemapOptions?: any[]
): string | null {
  // First try migration mapping
  const migrated = migrateBasemapId(identifier);
  if (migrated !== identifier) {
    return migrated;
  }

  // If basemapOptions provided, search through them
  if (basemapOptions && basemapOptions.length > 0) {
    const found = findBasemapByIdentifier(identifier, basemapOptions);
    if (found) {
      return found.id;
    }
  }

  // Return the identifier as-is (might be a valid UniqueKey)
  return identifier;
}

/**
 * Validates if a basemap identifier exists in the provided options
 * @param identifier - The basemap identifier to validate
 * @param basemapOptions - Array of basemap options from Redux state
 * @returns True if the identifier is valid
 */
export function isValidBasemapIdentifier(
  identifier: string,
  basemapOptions: any[]
): boolean {
  return findBasemapByIdentifier(identifier, basemapOptions) !== null;
}

/**
 * Resolves basemap config for map init before the MapView is created.
 * Uses feature-service options when available, otherwise static national fallbacks.
 */
export function resolveBasemapConfigForInit(
  baseMapId: string,
  basemapOptions: any[]
): BasemapInitConfig | null {
  const resolvedId = baseMapId || MAP_DEFAULT_VAR.BASEMAP;
  const options = basemapOptions || [];

  const basemapItem = findBasemapByIdentifier(resolvedId, options);
  if (basemapItem) {
    return {
      styleId: basemapItem.styleId,
      webmapId: basemapItem.webmapId
    };
  }

  const staticConfig = STATIC_NATIONAL_BASEMAP_CONFIGS[resolvedId];
  if (staticConfig) {
    return staticConfig;
  }

  if (options.length === 0) {
    return null;
  }

  return null;
}

/**
 * Builds an ArcGIS Basemap instance from a resolved init config.
 * Prefers styleId (sync) over webmapId (async portal load).
 */
export async function buildBasemapFromConfig(
  config: BasemapInitConfig,
  Basemap: any,
  BasemapStyle: any
): Promise<any> {
  if (config.styleId) {
    return new Basemap({
      style: new BasemapStyle({ id: config.styleId })
    });
  }

  if (config.webmapId) {
    const webmapBasemap = new Basemap({
      portalItem: { id: config.webmapId }
    });
    await webmapBasemap.load();
    return webmapBasemap;
  }

  return new Basemap({
    style: new BasemapStyle({ id: 'arcgis/imagery' })
  });
}
